import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useMediaQuery,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  TextField,
  Box,
  IconButton,
  FormHelperText,
  CircularProgress,
  Alert,
  Collapse
} from "@mui/material";
import { 
  ArrowDropDown, 
  Delete, 
  Edit, 
  Settings, 
  Close,
  CheckCircle,
  Error
} from "@mui/icons-material";
import Search from "../../../components/common/search/Search";
import Input from "../../../components/common/input/Input";
import axiosInstance from "../../../utils/axios/axiosInstance";
import API from "../../../config/apiconfig";
import { useDispatch } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";
import { toast } from "react-toastify";

const ExtraClassesTab = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [type, setType] = useState("");
  const [className, setClassName] = useState("");
  const [professor, setProfessor] = useState("");
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };
  
  // Helper function to get time 1 hour from now
  const getEndTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toTimeString().slice(0, 5);
  };
  
  const [date, setDate] = useState(getTodayDate()); // Default to today
  const [time, setTime] = useState(getCurrentTime()); // Default to current time
  const [endTime, setEndTime] = useState(getEndTime()); // Default to 1 hour later
  const [location, setLocation] = useState("");
  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const [settings, setSettings] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [sortedData, setSortedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Form validation functions
  const validateClassName = (name) => {
    const trimmedName = name?.trim() || '';
    
    if (!trimmedName) {
      return 'Class name is required';
    }
    if (trimmedName.length < 3) {
      return 'Class name must be at least 3 characters long';
    }
    if (trimmedName.length > 100) {
      return 'Class name must be less than 100 characters';
    }
    return null;
  };

  const validateProfessor = (name) => {
    const trimmedName = name?.trim() || '';
    
    if (!trimmedName) {
      return 'Professor name is required';
    }
    if (trimmedName.length < 2) {
      return 'Professor name must be at least 2 characters long';
    }
    return null;
  };

  const validateDate = (dateValue) => {
    if (!dateValue) {
      return 'Date is required';
    }
    
    const selectedDate = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return 'Date cannot be in the past';
    }
    
    return null;
  };

  const validateTime = (timeValue) => {
    if (!timeValue) {
      return 'Start time is required';
    }
    return null;
  };

  const validateEndTime = (endTimeValue) => {
    if (!endTimeValue) {
      return 'End time is required';
    }
    
    if (time && endTimeValue <= time) {
      return 'End time must be after start time';
    }
    
    return null;
  };

  const validateLocation = (locationValue) => {
    const trimmedLocation = locationValue?.trim() || '';
    
    if (!trimmedLocation) {
      return 'Location is required';
    }
    
    return null;
  };

  const validateType = (typeValue) => {
    const trimmedType = typeValue?.trim() || '';
    
    if (!trimmedType) {
      return 'Type is required';
    }
    
    return null;
  };

  // Combined form validation
  const validateForm = () => {
    const errors = {};
    
    const classNameError = validateClassName(className);
    if (classNameError) errors.className = classNameError;
    
    const professorError = validateProfessor(professor);
    if (professorError) errors.professor = professorError;
    
    const dateError = validateDate(date);
    if (dateError) errors.date = dateError;
    
    const timeError = validateTime(time);
    if (timeError) errors.time = timeError;
    
    const endTimeError = validateEndTime(endTime);
    if (endTimeError) errors.endTime = endTimeError;
    
    const locationError = validateLocation(location);
    if (locationError) errors.location = locationError;
    
    const typeError = validateType(type);
    if (typeError) errors.type = typeError;
    
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);
    
    return { isValid, errors };
  };

  // Real-time field validation
  const validateField = (fieldName, value) => {
    let error = null;
    
    switch (fieldName) {
      case 'className':
        error = validateClassName(value);
        break;
      case 'professor':
        error = validateProfessor(value);
        break;
      case 'date':
        error = validateDate(value);
        break;
      case 'time':
        error = validateTime(value);
        break;
      case 'endTime':
        error = validateEndTime(value);
        break;
      case 'location':
        error = validateLocation(value);
        break;
      case 'type':
        error = validateType(value);
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error;
  };

  // Mark field as touched
  const markFieldAsTouched = (fieldName) => {
    setValidationTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle form input changes with validation
  const handleInputChange = (field, value) => {
    switch (field) {
      case 'className':
        setClassName(value);
        break;
      case 'professor':
        setProfessor(value);
        break;
      case 'date':
        setDate(value);
        break;
      case 'time':
        setTime(value);
        break;
      case 'endTime':
        setEndTime(value);
        break;
      case 'location':
        setLocation(value);
        break;
      case 'type':
        setType(value);
        break;
      default:
        break;
    }
    
    // Mark field as touched and validate
    markFieldAsTouched(field);
    validateField(field, value);
  };

  // Helper function to get field error state
  const getFieldError = (fieldName) => {
    return validationTouched[fieldName] && validationErrors[fieldName];
  };

  // Helper function to check if field is valid
  const isFieldValid = (fieldName) => {
    return validationTouched[fieldName] && !validationErrors[fieldName];
  };

  // Validate form whenever form data changes
  useEffect(() => {
    if (Object.keys(validationTouched).length > 0) {
      validateForm();
    }
  }, [className, professor, date, time, endTime, location, type, validationTouched]);

  const handleSort = (column) => {
    setSelectedFilter(column);
    if (column === "") {
      setSortedData(data);
      return;
    }
    const sorted = [...sortedData].sort((a, b) => {
      if (a[column] < b[column]) return -1;
      if (a[column] > b[column]) return 1;
      return 0;
    });
    setSortedData(sorted);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredData = data.filter((row) =>
      Object.values(row).some((val) => {
        if (val == null) return false;
        return val.toString().toLowerCase().includes(value.toLowerCase());
      })
    );
    setSortedData(filteredData);
  };

  const getClassesList = () => {
    dispatch(userApiLoader({ isApiLoader: true }));
    axiosInstance
      .get(`${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.EXTRA_CLASSES}`)
      .then((res) => {
        setData(res?.data?.data);
        setSortedData(res?.data?.data || []);
        dispatch(userApiLoader({ isApiLoader: false }));

        //toast.success(res?.data?.message, { autoClose: 6000 });

      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Error fetching classes", {
          autoClose: 6000
        });
      });
  };

  useEffect(() => {
    getClassesList();
  }, []);

  const formatTimeToAmPm = (timeStr) => {
    if (!timeStr) return "";
    const [hour, minute] = timeStr.split(":").map(Number);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${ampm}`;
  };

  const createNewClass = () => {
    // Mark required fields as touched for validation display
    const requiredFields = ['className', 'professor', 'date', 'time', 'endTime', 'location', 'type'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    // Validate form before submission
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    setSubmitting(true);
    setSettings(false);
    dispatch(userApiLoader({ isApiLoader: true }));

    const dataPayload = {
      class_name: className,
      professor: professor,
      date: date,
      time: formatTimeToAmPm(time),
      end_time: formatTimeToAmPm(endTime),
      location: location,
      type: type
    };

    axiosInstance
      .post(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.EXTRA_CLASSES}`,
        dataPayload
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message || "Class created successfully!", { autoClose: 6000 });
        getClassesList();
        resetForm();
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Error creating class", {
          autoClose: 6000
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEdit = (classData) => {
    setIsEditing(true);
    setEditingId(classData.id);
    setSettings(true);
    setClassName(classData.class_name);
    setProfessor(classData.professor);
    setDate(classData.date);
    
    // convert stored "HH:MM AM/PM" to "HH:MM" (24-hour) for input[type=time]
    const convertTo24Hour = (time12h) => {
      if (!time12h) return "";
      const [timePart, modifier] = time12h.split(" ");
      let [hours, minutes] = timePart.split(":");
      if (modifier === "PM" && hours !== "12") {
        hours = String(parseInt(hours, 10) + 12);
      }
      if (modifier === "AM" && hours === "12") {
        hours = "00";
      }
      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    };
    
    setTime(convertTo24Hour(classData.time));
    setEndTime(convertTo24Hour(classData.end_time || ""));
    setLocation(classData.location || "");
    setType(classData.type || "");
    
    // Reset validation state for edit mode
    setValidationErrors({});
    setValidationTouched({});
  };

  const resetForm = () => {
    setClassName("");
    setProfessor("");
    setDate(getTodayDate()); // Reset to today
    setTime(getCurrentTime()); // Reset to current time
    setEndTime(getEndTime()); // Reset to 1 hour from now
    setLocation("");
    setType("");
    setEditingId(null);
    setIsEditing(false);
    setValidationErrors({});
    setValidationTouched({});
    setIsFormValid(false);
  };

  const updateClass = () => {
    // Mark required fields as touched for validation display
    const requiredFields = ['className', 'professor', 'date', 'time', 'endTime', 'location', 'type'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    // Validate form before submission
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    setSubmitting(true);
    setIsEditing(false);
    setSettings(false);
    dispatch(userApiLoader({ isApiLoader: true }));

    const dataPayload = {
      id: editingId,
      class_name: className,
      professor: professor,
      date: date,
      time: formatTimeToAmPm(time),
      end_time: formatTimeToAmPm(endTime),
      location: location,
      type: type
    };

    axiosInstance
      .put(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.EXTRA_CLASSES}`,
        dataPayload
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message || "Class updated successfully!", { autoClose: 6000 });
        getClassesList();
        resetForm();
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Error updating class", {
          autoClose: 6000
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const deleteClass = (row) => {
    dispatch(userApiLoader({ isApiLoader: true }));
    axiosInstance
      .delete(
        `${API.USER_MANAGEMENT_API_BASE_URL}${API.TOOLS}${API.EXTRA_CLASSES}/${row.id}`
      )
      .then((res) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.success(res?.data?.message || "Class deleted successfully!", { autoClose: 6000 });
        getClassesList();
      })
      .catch((err) => {
        dispatch(userApiLoader({ isApiLoader: false }));
        toast.error(err?.response?.data?.message || "Error deleting class", {
          autoClose: 6000
        });
      });
  };

  // Close modal handler
  const closeModal = () => {
    setSettings(false);
    resetForm();
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEditing) {
      updateClass();
    } else {
      createNewClass();
    }
  };

  return (
    <Grid container spacing={2}>

      <div className="container">
        <div class="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div class="card-body ">

                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div class="d-flex align-items-center">
                    <h3 class="mb-2">Extra Classes</h3>
                  </div>
                  <div class="d-flex align-items-center flex-wrap mb-1">
                    <Button variant="contained" color="primary" className="!w-fit !px-3 !h-10"
                      onClick={() => setSettings(true)}>
                      + Add New Class
                    </Button>
                  </div>
                </div>

                <div className="row filter-panel mt-4">
                  <div className="col-md-4">
                    <div className="serarch-bar">
                      <Search value={searchQuery} handleInputChange={(e) => handleSearch(e.target.value)} />
                    </div></div>
                  <div className="col-md-6"></div>
                  <div className="col-md-2">
                    <div className="w-full flex items-center justify-end gap-2">
                      <FormControl fullWidth className="filter">
                        <InputLabel
                        >
                          Filter
                        </InputLabel>
                        <Select
                          value={selectedFilter}
                          onChange={(e) => handleSort(e.target.value)}
                          label="Filter"
                          IconComponent={(props) => (
                            <ArrowDropDown {...props} style={{ color: "#c942b1" }} />
                          )}
                          style={{ height: 40 }}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="studentId">Student ID</MenuItem>
                          <MenuItem value="university">University</MenuItem>
                          <MenuItem value="courseName">Course Name</MenuItem>
                          <MenuItem value="dateTime">Date & Time</MenuItem>
                        </Select>
                      </FormControl>

                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div class="table-responsive mb-0">
                          <TableContainer className="table table-hover table-bordered mb-0 text-md-nowrap text-lg-nowrap text-xl-nowrap table-striped">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student ID</TableCell>
                  <TableCell>Class Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Professor</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.student_id}</TableCell>
                    <TableCell>{row.class_name}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{row.time}</TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell>{row.professor}</TableCell>
                    <TableCell align="center">
                       <div className="flex items-center gap-1 active-btn">
                        <div
                           className="btn btn-indigo btn-icon"
                          onClick={() => handleEdit(row)}
                        >
                          <Edit color="primary" fontSize="small" />
                        </div>
                        <div
                          className="btn btn-denger btn-icon"
                          onClick={() => deleteClass(row)}
                        >
                          <Delete color="primary" fontSize="small" />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Material-UI Drawer Form - Using StudyGroup style */}
      <Drawer
        anchor="right"
        open={settings}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '90vw',
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{
            background: '#ce55b9',
            color: 'white',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: '"Saira", sans-serif' }}>
                {isEditing ? 'Update' : 'Add'} Class
              </Typography>
              {/* Form validation indicator */}
              {Object.keys(validationTouched).length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isFormValid ? (
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <Error sx={{ color: '#ffeb3b', fontSize: 20 }} />
                  )}
                </Box>
              )}
            </Box>
            <IconButton
              onClick={closeModal}
              disabled={submitting}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflow: 'auto',
            background: '#ffffff'
          }}>
            <TextField
              fullWidth
              label="Class Name"
              value={className}
              onChange={(e) => handleInputChange('className', e.target.value)}
              onBlur={() => markFieldAsTouched('className')}
              required
              error={!!getFieldError('className')}
              helperText={getFieldError('className') || 'Enter the name of the class'}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('className') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('className') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif'
                }
              }}
              disabled={submitting}
              placeholder="Enter class name"
              InputProps={{
                endAdornment: isFieldValid('className') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('className') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <TextField
              fullWidth
              label="Professor"
              value={professor}
              onChange={(e) => handleInputChange('professor', e.target.value)}
              onBlur={() => markFieldAsTouched('professor')}
              required
              error={!!getFieldError('professor')}
              helperText={getFieldError('professor') || 'Enter the professor name'}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('professor') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('professor') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif'
                }
              }}
              disabled={submitting}
              placeholder="Enter professor name"
              InputProps={{
                endAdornment: isFieldValid('professor') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('professor') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <TextField
              fullWidth
              type="date"
              label="Date"
              value={date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              onBlur={() => markFieldAsTouched('date')}
              required
              error={!!getFieldError('date')}
              helperText={getFieldError('date') || `Select class date (Today: ${getTodayDate()})`}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: getTodayDate(), // Prevent selecting past dates
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('date') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('date') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif'
                }
              }}
              disabled={submitting}
              InputProps={{
                endAdornment: isFieldValid('date') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('date') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                value={time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                onBlur={() => markFieldAsTouched('time')}
                required
                error={!!getFieldError('time')}
                helperText={getFieldError('time') || `Select start time (Now: ${getCurrentTime()})`}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-error': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isFieldValid('time') ? '#4caf50' : '#ce55b9',
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-focused': {
                      color: isFieldValid('time') ? '#4caf50' : '#ce55b9'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: '"Saira", sans-serif'
                  }
                }}
                disabled={submitting}
                InputProps={{
                  endAdornment: isFieldValid('time') ? (
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : getFieldError('time') ? (
                    <Error sx={{ color: '#f44336', fontSize: 20 }} />
                  ) : null
                }}
              />

              <TextField
                fullWidth
                type="time"
                label="End Time"
                value={endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                onBlur={() => markFieldAsTouched('endTime')}
                required
                error={!!getFieldError('endTime')}
                helperText={getFieldError('endTime') || 'Select end time (Default: +1 hour)'}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-error': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isFieldValid('endTime') ? '#4caf50' : '#ce55b9',
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Saira", sans-serif',
                    '&.Mui-focused': {
                      color: isFieldValid('endTime') ? '#4caf50' : '#ce55b9'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: '"Saira", sans-serif'
                  }
                }}
                disabled={submitting}
                InputProps={{
                  endAdornment: isFieldValid('endTime') ? (
                    <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : getFieldError('endTime') ? (
                    <Error sx={{ color: '#f44336', fontSize: 20 }} />
                  ) : null
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Location"
              value={location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              onBlur={() => markFieldAsTouched('location')}
              required
              error={!!getFieldError('location')}
              helperText={getFieldError('location') || 'Enter the class location'}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('location') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('location') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif'
                }
              }}
              disabled={submitting}
              placeholder="Enter location"
              InputProps={{
                endAdornment: isFieldValid('location') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('location') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <TextField
              fullWidth
              label="Type"
              value={type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              onBlur={() => markFieldAsTouched('type')}
              required
              error={!!getFieldError('type')}
              helperText={getFieldError('type') || 'Enter the class type (e.g., Lecture, Lab, Tutorial)'}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('type') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('type') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif'
                }
              }}
              disabled={submitting}
              placeholder="Enter class type"
              InputProps={{
                endAdornment: isFieldValid('type') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('type') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />
          </Box>

          {/* Footer Actions */}
          <Box sx={{
            p: 3,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'white',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={closeModal}
              disabled={submitting}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || (!isFormValid && Object.keys(validationTouched).length > 0)}
              sx={{
                background: isFormValid ? '#4caf50' : '#ce55b9',
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 140,
                '&:hover': {
                  background: isFormValid ? '#b8479a' : '#b8479a',
                },
                '&:disabled': {
                  background: '#b8479a',
                  color: 'white'
                }
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               
                  <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isFormValid && <CheckCircle sx={{ fontSize: 18 }} />}
                  <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {isEditing ? 'Update' : 'Add Class'}
                  </Typography>
                </Box>
              )}
            </Button>
          </Box>
        </form>
      </Drawer>
    </Grid>
  );
};

export default ExtraClassesTab;